import React, { useEffect } from 'react';
import { CustomTooltip } from './CustomTooltip';
import { createRoot } from 'react-dom/client';
import { Brand } from '../../../embedding/dwt-app';

const RICH_TEXT_TOOLTIP_CLASS = 'richtext-tooltip';
export const GLOBAL_TOOLTIP_CLASS = 'global-tooltip';

function useGlobalTooltipChecker(brandName: Brand) {
  useEffect(() => {
    const intervalId = setInterval(() => {
      findTooltipElements(document.body);
    }, 10);

    function findTooltipElements(root: any) {
      if (
        root instanceof HTMLElement &&
        (root.classList.contains(RICH_TEXT_TOOLTIP_CLASS) || root.classList.contains(GLOBAL_TOOLTIP_CLASS))
      ) {
        attachTooltipEvents(root);
      }

      root.childNodes.forEach((child: any) => {
        if (child.nodeType === 1) {
          findTooltipElements(child);
        }
      });

      if (root instanceof HTMLElement && root.shadowRoot) {
        root.shadowRoot.childNodes.forEach((child) => {
          if (child.nodeType === 1) {
            findTooltipElements(child);
          }
        });
      }
    }

    function attachTooltipEvents(element: any) {
      if (!element.hasAttribute('data-tooltip-initialized')) {
        element.addEventListener('mouseenter', handleMouseEnter);
        element.addEventListener('mouseleave', handleMouseLeave);
        element.setAttribute('data-tooltip-initialized', 'true');
      }
    }

    let isTooltipVisible = false;
    let isTooltipBig = false;
    let hoverTimeout: any;
    let tooltipWrapper: HTMLElement | null = null;

    function handleMouseEnter(event: any) {
      if (isTooltipVisible) return;

      hoverTimeout = setTimeout(() => {
        const tooltipText = event.target.getAttribute('data-tooltip-html');
        if (tooltipText) {
          isTooltipVisible = true;
          const computedStyles = getComputedStyle(event.target);
          const targetStyles = {
            fontFamily: computedStyles.fontFamily,
            color: computedStyles.color,
          };
          showTooltip(event.target, tooltipText, targetStyles);
        }
      }, 100);
    }

    function handleMouseLeave(event: any) {
      clearTimeout(hoverTimeout);

      hoverTimeout = setTimeout(() => {
        const related = event.relatedTarget as Node | null;

        if (tooltipWrapper && related && tooltipWrapper.contains(related)) {
          return;
        }

        closeTooltip();
      }, 200);
    }

    function closeTooltip() {
      if (activeTooltipRemover) {
        activeTooltipRemover();
      }
      isTooltipVisible = false;
      isTooltipBig = false;
    }

    function handleTooltipMouseEnter() {
      clearTimeout(hoverTimeout);
    }

    function handleTooltipMouseLeave(event: any) {
      const related = event.relatedTarget as Node | null;

      if (!related || (!tooltipWrapper?.contains(related) && !event.target?.contains(related))) {
        hoverTimeout = setTimeout(() => {
          closeTooltip();
        }, 200);
      }
    }

    function adjustTooltipPosition(tooltipWrapperInner: any, target: any) {
      const { width: tooltipWidth, height: tooltipHeight } = tooltipWrapperInner.getBoundingClientRect();
      const viewportWidth = window.innerWidth;

      let { left, width: targetWidth } = target.getBoundingClientRect();
      const { top, height: targetHeight } = target.getBoundingClientRect();
      let tooltipLeft = left + targetWidth / 2 - tooltipWidth / 2;
      let tooltipTop = top + window.scrollY - tooltipHeight - 12;
      let isTooltipAbove = true;

      if (tooltipTop < window.scrollY) {
        tooltipTop = top + window.scrollY + targetHeight + 12;
        isTooltipAbove = false;
      }

      if (tooltipLeft + tooltipWidth > viewportWidth) {
        tooltipLeft = viewportWidth - tooltipWidth - 10;
        isTooltipBig = true;
      }
      if (tooltipLeft < 10) {
        tooltipLeft = 10;
        isTooltipBig = true;
      }

      tooltipWrapperInner.style.left = `${tooltipLeft}px`;
      tooltipWrapperInner.style.top = `${tooltipTop}px`;

      const arrowElement = tooltipWrapperInner.querySelector('.tooltip-arrow');
      if (arrowElement) {
        ({ left, width: targetWidth } = target.getBoundingClientRect());
        const arrowPosition = isTooltipBig ? left - 3 + targetWidth / 2 - tooltipLeft : tooltipWidth / 2 - 4;
        arrowElement.style.left = `${arrowPosition}px`;

        if (isTooltipAbove) {
          arrowElement.style.bottom = '-4px';
          arrowElement.style.top = '';
          arrowElement.style.transform = 'rotate(45deg)';
        } else {
          arrowElement.style.top = '-4px';
          arrowElement.style.bottom = '';
          arrowElement.style.transform = 'rotate(225deg)';
        }

        if (isTooltipBig) {
          tooltipWrapperInner.style.width = `calc(100% - 20px)`;
        }
      }
    }

    let activeTooltipRemover: any = null;

    function showTooltip(target: any, tooltipText: string, styles: any) {
      if (activeTooltipRemover) activeTooltipRemover();

      tooltipWrapper = document.createElement('div');
      tooltipWrapper.style.position = 'absolute';
      tooltipWrapper.style.pointerEvents = 'auto'; // Engedélyezzük az egéreseményeket
      tooltipWrapper.style.zIndex = '99999';
      document.body.appendChild(tooltipWrapper);
      const root = createRoot(tooltipWrapper);
      root.render(<CustomTooltip brand={brandName} text={tooltipText} styles={styles} />);

      setTimeout(() => {
        const tooltipElement = tooltipWrapper!.firstChild as HTMLElement;
        if (tooltipElement) {
          tooltipElement.classList.add('visible');
          tooltipElement.classList.add('richtext-tooltip');

          tooltipElement.addEventListener('mouseenter', handleTooltipMouseEnter);
          tooltipElement.addEventListener('mouseleave', handleTooltipMouseLeave);
        }
        adjustTooltipPosition(tooltipWrapper, target);
      }, 0);

      activeTooltipRemover = () => {
        root.unmount();
        tooltipWrapper!.remove();
        activeTooltipRemover = null;
        tooltipWrapper = null;
      };

      return activeTooltipRemover;
    }

    return () => {
      clearInterval(intervalId);
      document.querySelectorAll('.' + RICH_TEXT_TOOLTIP_CLASS).forEach((tooltip) => {
        tooltip.removeEventListener('mouseenter', handleMouseEnter);
        tooltip.removeEventListener('mouseleave', handleMouseLeave);
      });
    };
  }, [brandName]);
}

export default useGlobalTooltipChecker;
